import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    americanFootballOutline,
    refreshOutline,
    statsChartOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    GameDetail,
    GamePlay,
    PlayersById,
    Team,
    getGameDetail,
    getPlayers,
    getTeams
} from "../services/mexfbaApi";
import "./GamePlays.css";

interface RouteParams {
    id: string;
}

interface TimelinePlay {
    play: GamePlay;
    index: number;
    localScore: number;
    visitorScore: number;
    scoringPoints: number;
    scoringTeamId: string;
    down: string;
}


function getPlayerName(
    playerId: string | undefined,
    players: PlayersById
): string {
    if (!playerId) {
        return "";
    }

    return players[playerId]?.nombre || "Jugador";
}

function getNumber(value: string | undefined): number {
    const numberValue = Number(value);

    return Number.isFinite(numberValue) ? numberValue : 0;
}

function getPoints(play: GamePlay): number {
    const touchdown = getNumber(play.td);
    const defensiveTouchdown = getNumber(play.deftd);
    const returnTouchdown = getNumber(play.retTd);

    if (touchdown > 0) {
        return touchdown;
    }

    if (defensiveTouchdown > 0) {
        return defensiveTouchdown;
    }

    if (returnTouchdown > 0) {
        return returnTouchdown;
    }

    return 0;
}

function getScoringTeamId(
    play: GamePlay,
    localTeamId: string,
    visitorTeamId: string
): string {
    const isDefensiveScore = getNumber(play.deftd) > 0;
    const isReturnScore = getNumber(play.retTd) > 0;

    /*
        En una anotación defensiva o de regreso,
        anota el rival de quien tenía la ofensiva.
    */
    if (isDefensiveScore || isReturnScore) {
        return play.ofensiva === localTeamId
            ? visitorTeamId
            : localTeamId;
    }

    return play.ofensiva || "";
}
interface DownState {
    currentOffense: string;
    yardsToGo: number;
    penaltyFlag: boolean;
    onGoal: boolean;
    pendingLossYards: boolean;
    previousSpot: number;
    previousSide: string;
}

function hasValue(value?: string | null): boolean {
    return value !== undefined && value !== null && value !== "";
}

function getSafeNumber(value?: string): number {
    const parsedValue = Number(value);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function createInitialDownState(): DownState {
    return {
        currentOffense: "",
        yardsToGo: 10,
        penaltyFlag: false,
        onGoal: false,
        pendingLossYards: false,
        previousSpot: 0,
        previousSide: ""
    };
}

function getTeamSide(
    offenseId: string | undefined,
    side: string | undefined,
    od: string | undefined,
    localTeamId: string,
    localTeamName: string,
    visitorTeamName: string
): string {
    const normalizedOd = hasValue(od) ? od : "0";
    const isOffenseReference = normalizedOd === "0";
    const isLocalOffense = offenseId === localTeamId;
    const isHomeSide = side === "0";

    if (isOffenseReference) {
        if (isLocalOffense) {
            return isHomeSide ? localTeamName : visitorTeamName;
        }

        return isHomeSide ? visitorTeamName : localTeamName;
    }

    if (isLocalOffense) {
        return isHomeSide ? visitorTeamName : localTeamName;
    }

    return isHomeSide ? localTeamName : visitorTeamName;
}


function getFieldPosition(
    play: GamePlay,
    localTeamId: string,
    localTeamName: string,
    visitorTeamName: string
): string {
    if (!hasValue(play.ydsI)) {
        return "";
    }

    const side = getTeamSide(
        play.ofensiva,
        play.side,
        play.od,
        localTeamId,
        localTeamName,
        visitorTeamName
    );

    return ` en ${side} ${play.ydsI}`;
}

function getDownLabel(
    play: GamePlay,
    state: DownState,
    localTeamId: string,
    localTeamName: string,
    visitorTeamName: string
): string {
    const down = String(play.d || "");

    if (!down) {
        return "";
    }

    /*
        El código anterior no calculaba distancia en conversiones.
    */
    if (down === "5") {
        return "Intento de conversión";
    }

    /*
        Kickoff: reinicia la condición de gol, igual que el código anterior.
    */
    if (down === "0") {
        state.onGoal = false;
        state.yardsToGo = 10;
        state.penaltyFlag = false;

        return "Kickoff";
    }

    const yards = getSafeNumber(play.yds);
    const spot = getSafeNumber(play.ydsI);
    const hasYards = hasValue(play.yds);
    const hasPenalty = hasValue(play.C);
    const isOffensivePenalty = play.od === "0";

    /*
        Esta es la distancia que se debe mostrar para ESTA jugada.
        Las modificaciones posteriores aplican para la siguiente jugada.
    */
    let displayYards = state.yardsToGo;

    /*
        Recupera yardas perdidas por sack o fumble sin recuperación.
        El sistema anterior calculaba esas yardas a partir de la posición
        de la siguiente jugada registrada.
    */
    if (state.pendingLossYards) {
        let lostYards = 0;

        if (
            state.previousSide === "0" &&
            play.side === "0"
        ) {
            lostYards = state.previousSpot - spot;
        } else if (
            state.previousSide === "1" &&
            play.side === "1"
        ) {
            lostYards = spot - state.previousSpot;
        } else {
            lostYards =
                (50 - state.previousSpot) +
                (50 - spot);
        }

        state.yardsToGo += lostYards;
        displayYards = state.yardsToGo;
        state.pendingLossYards = false;
    }

    /*
        Primera y gol:
        - Primera oportunidad.
        - Balón dentro de la yarda 10.
        - Referencia en territorio rival (side === "1").
    */
    if (
        down === "1" &&
        spot < 11 &&
        play.side === "1"
    ) {
        state.onGoal = true;
    } else if (
        state.currentOffense !== play.ofensiva &&
        play.od === "0"
    ) {
        /*
            Cambio de posesión por castigo ofensivo:
            se elimina la condición anterior de gol.
        */
        state.onGoal = false;
    }

    const isNewPossession =
        down === "1" &&
        state.currentOffense !== play.ofensiva;

    /*
        Nueva posesión.
        El código anterior solamente la consideraba nueva serie cuando:
        - no hay castigo, o
        - hay castigo ofensivo.
    */
    if (isNewPossession) {
        if (!hasPenalty || isOffensivePenalty) {
            state.currentOffense = play.ofensiva || "";
            state.yardsToGo = 10;
            displayYards = 10;
            state.penaltyFlag = hasPenalty && isOffensivePenalty;
        }
    }

    if (hasYards) {
        /*
            Una primera oportunidad sin castigo pendiente representa una
            nueva serie: 1ª y 10 antes de aplicar el resultado de la jugada.
        */
        if (down === "1" && !state.penaltyFlag) {
            state.yardsToGo = 10;
            displayYards = 10;
            state.penaltyFlag = false;

            if (hasPenalty) {
                state.penaltyFlag = true;

                if (isOffensivePenalty) {
                    /*
                        No usar Math.abs:
                        se conserva el comportamiento original.
                    */
                    state.yardsToGo += yards;
                } else if (state.yardsToGo - yards >= 0) {
                    state.yardsToGo -= yards;
                }
            } else {
                // Una jugada normal no deja un castigo pendiente.
                state.penaltyFlag = false;

                if (state.yardsToGo - yards >= 0) {
                    state.yardsToGo -= yards;
                }
            }
        } else if (hasPenalty) {
            /*
                Castigo en una jugada posterior.
                Un castigo en primera oportunidad mantiene la bandera
                para evitar reiniciar incorrectamente la distancia.
            */
            state.penaltyFlag = down === "1";

            if (isOffensivePenalty) {
                state.yardsToGo += yards;
            } else if (state.yardsToGo - yards >= 0) {
                state.yardsToGo -= yards;
            }
        } else {
            /*
                Jugada normal: una ganancia reduce la distancia y una pérdida
                la aumenta automáticamente porque yards puede ser negativo.
            */
            state.penaltyFlag = false;

            if (state.yardsToGo - yards >= 0) {
                state.yardsToGo -= yards;
            }
        }
    } else {
        /*
            Jugadas sin valor en yds: conserva la regla original para cambios
            de posesión o una nueva primera oportunidad sin avance registrado.
        */
        if (isNewPossession) {
            if (!hasPenalty || isOffensivePenalty) {
                state.currentOffense = play.ofensiva || "";
                state.yardsToGo = 10;
                displayYards = 10;
                state.penaltyFlag =
                    hasPenalty && isOffensivePenalty;
            }
        } else if (down === "1" && !state.penaltyFlag) {
            if (!hasPenalty || isOffensivePenalty) {
                state.currentOffense = play.ofensiva || "";
                state.yardsToGo = 10;
                displayYards = 10;
                state.penaltyFlag =
                    hasPenalty && isOffensivePenalty;
            }
        }
    }

    /*
        El sack se ajusta en la siguiente jugada usando la posición de campo.
    */
    if (hasValue(play.sack)) {
        state.pendingLossYards = true;
        state.previousSpot = spot;
        state.previousSide = play.side || "";
    } else if (
        hasValue(play.fumbleF) &&
        !hasValue(play.fumbleR)
    ) {
        /*
            El código antiguo usaba ydsF para fumble no recuperado.
        */
        state.pendingLossYards = true;
        state.previousSpot = getSafeNumber(play.ydsF);
        state.previousSide = play.side || "";
    }

    const distance = state.onGoal
        ? "Gol"
        : displayYards === 0
        ? "Pulgadas"
        : String(displayYards);

    return `${down}ª & ${distance}${getFieldPosition(
        play,
        localTeamId,
        localTeamName,
        visitorTeamName
    )}`;
}


function getAssistedTackle(
    playerId: string | undefined,
    players: PlayersById
): string {
    const playerName = getPlayerName(playerId, players);

    return playerName ? `, asistido por ${playerName}` : "";
}

function getTackleText(
    play: GamePlay,
    players: PlayersById
): string {
    const tackler = getPlayerName(play.tkl, players);

    if (!tackler) {
        return "";
    }

    return ` Tackle de ${tackler}${getAssistedTackle(
        play.assTkl,
        players
    )}${getAssistedTackle(play.assTkll, players)}.`;
}

function getFumbleText(
    play: GamePlay,
    players: PlayersById
): string {
    const forcedBy = getPlayerName(play.fumbleF, players);
    const recoveredBy = getPlayerName(play.fumbleR, players);

    if (!forcedBy && !recoveredBy) {
        return "";
    }

    let description = " Fumble.";

    if (forcedBy) {
        description += ` Forzado por ${forcedBy}.`;
    }

    if (recoveredBy) {
        description += ` Recuperado por ${recoveredBy}.`;
    }

    return description;
}

function getScoreDescription(play: GamePlay): string {
    const touchdown = getNumber(play.td);
    const defensiveTouchdown = getNumber(play.deftd);
    const returnTouchdown = getNumber(play.retTd);

    if (touchdown === 6) {
        return " Touchdown.";
    }

    if (defensiveTouchdown === 6) {
        return " Touchdown defensivo.";
    }

    if (returnTouchdown === 6) {
        return " Touchdown de regreso.";
    }

    if (touchdown === 3) {
        return " Gol de campo bueno.";
    }

    if (touchdown === 2) {
        return " Conversión de dos puntos buena.";
    }

    if (touchdown === 1) {
        return " Punto extra bueno.";
    }

    if (defensiveTouchdown === 2) {
        return " Safety.";
    }

    if (play.d === "5" && touchdown === 0) {
        return " Conversión no lograda.";
    }

    /*
        td = "0" en una patada indica un intento de gol de campo fallido.
        Se valida el valor original para no confundirlo con un despeje,
        donde td normalmente llega vacío.
    */
    if (play.jugada === "2" && play.td === "0") {
        return " Gol de campo fallido.";
    }

    return "";
}

function getPlayType(play: GamePlay): string {
    if (getNumber(play.td) === 6) {
        return "Touchdown";
    }

    if (getNumber(play.deftd) === 6) {
        return "Touchdown defensivo";
    }

    if (getNumber(play.retTd) === 6) {
        return "Touchdown de regreso";
    }

    if (getNumber(play.td) === 3) {
        return "Gol de campo";
    }

    if (getNumber(play.td) === 2) {
        return "Conversión de dos puntos";
    }

    if (getNumber(play.td) === 1) {
        return "Punto extra";
    }

    if (getNumber(play.deftd) === 2) {
        return "Safety";
    }

    if (play.int) {
        return "Intercepción";
    }

    if (play.sack) {
        return "Captura";
    }

    if (play.fumbleF || play.fumbleR) {
        return "Fumble";
    }

    if (play.jugada === "0") {
        return "Carrera";
    }

    if (play.jugada === "1") {
        return "Pase";
    }

    if (play.jugada === "2") {
      if (play.C != "") {
        return "Castigo";
      }
        return "Patada";
    }

    return "Jugada";
}

function getPlayDescription(
    play: GamePlay,
    players: PlayersById
): string {
    const quarterback = getPlayerName(play.QB, players);
    const runner = getPlayerName(play.RB, players);
    const receiver = getPlayerName(play.WR, players);
    const kicker = getPlayerName(play.K, players);
    const interceptor = getPlayerName(play.int, players);
    const sacker = getPlayerName(play.sack, players);
    const returner = getPlayerName(play.ret, players);
    const penaltyPlayer = getPlayerName(play.playerC, players);
    const pitchPlayer = getPlayerName(play.pitch, players);
    const passDefender = getPlayerName(play.passDef, players);
    const blockedBy = getPlayerName(play.kickBlq, players);

    const yards = play.yds || "0";
    const isLongPass = play.longPass === "1";
    const longPassText = isLongPass ? " largo" : "";
    const tackleText = getTackleText(play, players);
    const fumbleText = getFumbleText(play, players);
    const scoreText = getScoreDescription(play);

    /*
        Intercepción: tiene prioridad sobre pase normal.
    */
    if (hasValue(play.int)) {
        return `${quarterback || "El quarterback"} lanza un pase${longPassText} interceptado por ${
            interceptor || "la defensiva"
        }.${scoreText}${fumbleText}`;
    }

    /*
        Sack: no debe caer en la descripción de pase incompleto.
    */
    if (hasValue(play.sack)) {
        const assistedSack = getAssistedTackle(
            play.assSack,
            players
        );

        const secondAssistedSack = getAssistedTackle(
            play.assSackk,
            players
        );

        return `${quarterback || "El quarterback"} es capturado por ${
            sacker || "la defensiva"
        }${assistedSack}${secondAssistedSack}.${scoreText}${fumbleText}`;
    }

    /*
        Carrera.
    */
    if (play.jugada === "0") {
        let description = `${runner || "El corredor"} corre para ${yards} yardas.`;

        if (hasValue(play.pitch)) {
            if (play.pf === "1") {
                description += ` Pasa el balón a ${
                    pitchPlayer || "un compañero"
                } para ${play.ydsPitch || "0"} yardas adicionales.`;
            }

            if (play.pf === "2") {
                description += ` Pierde el balón y ${
                    pitchPlayer || "un jugador"
                } lo recupera.`;
            }
        }

        return `${description}${scoreText}${tackleText}${fumbleText}`;
    }

    /*
        Pase completo o incompleto.
    */
    if (play.jugada === "1") {
        const isComplete =
            hasValue(play.WR) &&
            hasValue(play.yds);

        if (isComplete) {
            let description = `${quarterback || "El quarterback"} completa pase${longPassText} con ${
                receiver || "el receptor"
            } para ${yards} yardas.`;

            if (hasValue(play.pitch)) {
                if (play.pf === "1") {
                    description += ` Pasa el balón a ${
                        pitchPlayer || "un compañero"
                    } para ${play.ydsPitch || "0"} yardas adicionales.`;
                }

                if (play.pf === "2") {
                    description += ` Pierde el balón y ${
                        pitchPlayer || "un jugador"
                    } lo recupera.`;
                }
            }

            return `${description}${scoreText}${tackleText}${fumbleText}`;
        }

        const intendedFor = receiver
            ? ` para ${receiver}`
            : "";

        const droppedPass =
            play.completo === "2" ? " El pase es soltado." : "";

        const defendedPass = passDefender
            ? ` Pase defendido por ${passDefender}.`
            : "";

        return `${quarterback || "El quarterback"} lanza un pase${longPassText} incompleto${intendedFor}.${droppedPass}${defendedPass}`;
    }

    /*
        Kickoff, punt, gol de campo, punto extra y castigos.
    */
    if (play.jugada === "2") {
        if (hasValue(play.C)) {
            const side =
                play.od === "0" ? "ofensiva" : "defensiva";

            return `Castigo de ${yards} yardas${
                penaltyPlayer
                    ? ` sobre ${penaltyPlayer}`
                    : ""
            } a la ${side}.${scoreText}`;
        }

        if (
            getNumber(play.deftd) === 2 &&
            !hasValue(play.fumbleR) &&
            !hasValue(play.int)
        ) {
            return `Safety.${scoreText}`;
        }

        if (hasValue(play.kickBlq)) {
            return `${kicker || "El pateador"} intenta una patada bloqueada por ${
                blockedBy || "la defensiva"
            }.${scoreText}${fumbleText}`;
        }

        if (getNumber(play.td) === 3) {
            return `Gol de campo de ${
                kicker || "el pateador"
            }.${scoreText}`;
        }

        if (getNumber(play.td) === 1) {
            return `Punto extra intentado por ${
                kicker || "el pateador"
            }.${scoreText}`;
        }

        const kickAction =
            play.d === "0" ? "realiza un kickoff" : "patea";

        let description = `${kicker || "El pateador"} ${kickAction} para ${yards} yardas.`;

        if (play.muff === "1") {
            description += " Balón muffed.";
        }

        if (hasValue(play.ret)) {
            description += ` ${
                returner || "El regresador"
            } regresa la patada para ${play.retYds || "0"} yardas.`;
        }

        if (hasValue(play.pitch)) {
            if (play.pf === "1") {
                description += ` Pasa el balón a ${
                    pitchPlayer || "un compañero"
                } para ${play.ydsPitch || "0"} yardas adicionales.`;
            }

            if (play.pf === "2") {
                description += ` Fumble recuperado por ${
                    pitchPlayer || "un jugador"
                }.`;
            }
        }

        return `${description}${scoreText}${tackleText}${fumbleText}`;
    }

    /*
        Recuperación de fumble que no entró en las reglas anteriores.
    */
    if (hasValue(play.fumbleR)) {
        return `Fumble recuperado por ${getPlayerName(
            play.fumbleR,
            players
        )}.${scoreText}`;
    }

    return "Jugada registrada sin descripción disponible.";
}

const GamePlays: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [gameDetail, setGameDetail] =
        useState<GameDetail | null>(null);
    const [teams, setTeams] = useState<Team[]>([]);
    const [players, setPlayers] = useState<PlayersById>({});
    const [activeQuarter, setActiveQuarter] = useState("todos");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadGame = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [detail, teamsData, playersData] =
                    await Promise.all([
                        getGameDetail(id, forceRefresh),
                        getTeams(forceRefresh),
                        getPlayers(forceRefresh)
                    ]);

                /*
                    Reemplaza por completo los datos anteriores.
                    Así se reflejan nuevas jugadas o nuevos marcadores.
                */
                setGameDetail(detail);
                setTeams(teamsData);
                setPlayers(playersData);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar las jugadas:",
                    requestError
                );

                setError(
                    "No fue posible cargar las jugadas de este partido."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadGame();
        },
        [loadGame]
    );

    const teamsById = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [String(team.id), team];
            })
        );
    }, [teams]);

    const plays = useMemo(function (): GamePlay[] {
        if (!gameDetail?.Jugadas) {
            return [];
        }

        return Array.isArray(gameDetail.Jugadas)
            ? gameDetail.Jugadas
            : Object.values(gameDetail.Jugadas);
    }, [gameDetail]);

    const timeline = useMemo(function (): TimelinePlay[] {
    if (!gameDetail) {
        return [];
    }

    const localTeamId = gameDetail.Data.id_local;
    const visitorTeamId = gameDetail.Data.id_visita;

    const downState = createInitialDownState();

    let localScore = 0;
    let visitorScore = 0;

    return plays.map(function (play, index) {
        const scoringPoints = getPoints(play);

        const scoringTeamId = getScoringTeamId(
            play,
            localTeamId,
            visitorTeamId
        );

        if (scoringPoints > 0) {
            if (scoringTeamId === localTeamId) {
                localScore += scoringPoints;
            } else if (scoringTeamId === visitorTeamId) {
                visitorScore += scoringPoints;
            }
        }

        const down = getDownLabel(
    play,
    downState,
    localTeamId,
    gameDetail.Data.nombre_local || "Local",
    gameDetail.Data.nombre_visita || "Visita"
);


        return {
            play,
            index,
            localScore,
            visitorScore,
            scoringPoints,
            scoringTeamId,
            down
        };
    });
}, [gameDetail, plays]);


    const quarters = useMemo(function (): string[] {
    const values = new Set<string>(
        plays
            .map(function (play) {
                return play.q;
            })
            .filter(function (
                quarter
            ): quarter is string {
                return Boolean(quarter);
            })
    );

    return Array.from(values).sort(function (first, second) {
        return Number(first) - Number(second);
    });
}, [plays]);


    const filteredTimeline = useMemo(
        function () {
            if (activeQuarter === "todos") {
                return timeline;
            }

            return timeline.filter(function (item) {
                return item.play.q === activeQuarter;
            });
        },
        [activeQuarter, timeline]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadGame(true);

        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Jugadas</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="game-plays-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !gameDetail) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Jugadas</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="game-plays-content">
                    <div className="game-plays-empty">
                        <IonIcon icon={americanFootballOutline} />
                        <strong>No fue posible cargar las jugadas</strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadGame(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const game = gameDetail.Data;
    const localTeam = teamsById.get(String(game.id_local));
    const visitorTeam = teamsById.get(String(game.id_visita));

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="game-plays-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar al partido"
                            onClick={function () {
                                history.push(`/game/${id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Jugadas</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar jugadas"
                            disabled={refreshing}
                            onClick={function () {
                                loadGame(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="game-plays-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Descargando nuevas jugadas..."
                    />
                </IonRefresher>

                <section className="game-plays-score">
                    <button
                        type="button"
                        onClick={function () {
                            history.push(`/team/${game.id_local}`);
                        }}
                    >
                        {localTeam?.logo ? (
                            <img
                                src={localTeam.logo}
                                alt={`Logo de ${game.nombre_local}`}
                            />
                        ) : (
                            <span>
                                {game.nombre_local?.charAt(0) || "L"}
                            </span>
                        )}

                        <strong>{game.nombre_local}</strong>
                    </button>

                    <div>
                        <strong>{game.resLocal || "0"}</strong>
                        <span>—</span>
                        <strong>{game.resVisita || "0"}</strong>
                    </div>

                    <button
                        type="button"
                        onClick={function () {
                            history.push(`/team/${game.id_visita}`);
                        }}
                    >
                        {visitorTeam?.logo ? (
                            <img
                                src={visitorTeam.logo}
                                alt={`Logo de ${game.nombre_visita}`}
                            />
                        ) : (
                            <span>
                                {game.nombre_visita?.charAt(0) || "V"}
                            </span>
                        )}

                        <strong>{game.nombre_visita}</strong>
                    </button>
                </section>
<nav className="game-plays-navigation">
    <IonButton
        fill="clear"
        onClick={function () {
            history.push(`/game/${id}`);
        }}
    >
        Datos
    </IonButton>

    <IonButton
        fill="solid"
        onClick={function () {
            history.push(`/gamePlays/${id}`);
        }}
    >
        Jugadas
    </IonButton>

    <IonButton
        fill="clear"
        onClick={function () {
            history.push(`/gamePlayers/${id}`);
        }}
    >
        Jugadores
    </IonButton>

    <IonButton
        fill="clear"
        onClick={function () {
            history.push(`/gameStats/${id}`);
        }}
    >
        Stats
    </IonButton>
</nav>

                <section className="game-plays-tabs">
                    <IonButton
                        fill={
                            activeQuarter === "todos"
                                ? "solid"
                                : "outline"
                        }
                        onClick={function () {
                            setActiveQuarter("todos");
                        }}
                    >
                        Todos
                    </IonButton>

                    {quarters.map(function (quarter) {
                        return (
                            <IonButton
                                key={quarter}
                                fill={
                                    activeQuarter === quarter
                                        ? "solid"
                                        : "outline"
                                }
                                onClick={function () {
                                    setActiveQuarter(quarter);
                                }}
                            >
                                {quarter}Q
                            </IonButton>
                        );
                    })}
                </section>

                <section className="game-plays-list">
                    <div className="game-plays-list__heading">
                        <div>
                            <span>CRONOLOGÍA</span>
                            <h2>
                                {activeQuarter === "todos"
                                    ? "Todas las jugadas"
                                    : `Cuarto ${activeQuarter}`}
                            </h2>
                        </div>

                        <IonIcon icon={statsChartOutline} />
                    </div>

                    {filteredTimeline.length === 0 ? (
                        <div className="game-plays-empty-list">
                            No hay jugadas disponibles para este filtro.
                        </div>
                    ) : (
                        filteredTimeline.map(function (item) {
                            const offensiveTeam =
                                teamsById.get(
                                    String(item.play.ofensiva)
                                );

                            const isScoringPlay =
                                item.scoringPoints > 0;

                            return (
                                <article
                                    className={`game-play-card ${
                                        isScoringPlay
                                            ? "game-play-card--score"
                                            : ""
                                    }`}
                                    key={`${item.index}-${item.play.q}`}
                                >
                                  {item.down && (
                                      <div className="game-play-card__down">
                                          {item.down}
                                      </div>
                                  )}

                                    <div className="game-play-card__top">
                                        <IonBadge>
                                            {item.play.q
                                                ? `${item.play.q}Q`
                                                : "—"}
                                        </IonBadge>

                                        <span>
                                            {getPlayType(item.play)}
                                        </span>

                                        <strong>
                                            {item.localScore} -{" "}
                                            {item.visitorScore}
                                        </strong>
                                    </div>

                                    <div className="game-play-card__body">
                                        {offensiveTeam?.logo ? (
                                            <img
                                                src={offensiveTeam.logo}
                                                alt=""
                                            />
                                        ) : (
                                            <IonIcon
                                                icon={americanFootballOutline}
                                            />
                                        )}
                                        <p>
                                            {getPlayDescription(
                                                item.play,
                                                players
                                            )}
                                        </p>
                                    </div>

                                    {isScoringPlay && (
                                        <div className="game-play-card__score">
                                            +{item.scoringPoints} puntos
                                        </div>
                                    )}
                                </article>
                            );
                        })
                    )}
                </section>
            </IonContent>
        </IonPage>
    );
};

export default GamePlays;
